import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Users, CheckCircle2, AlertTriangle, Clock, ArrowUpRight, Wrench, TrendingUp, LogIn, LogOut, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, expiryBadge, ROLE_LABELS } from "@/lib/helpers";
import { BOOKING_STATUS_CLS } from "@/components/Bookings";
import { Progress } from "@/components/ui/progress";
import TenantHome from "@/pages/TenantHome";

const Stat = ({ label, value, icon: Icon, accent }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white border border-border rounded-md p-4 sm:p-5"
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="overline text-muted-foreground leading-tight">{label}</p>
        <p className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mt-1.5 sm:mt-2">{value}</p>
      </div>
      <div className={`h-8 w-8 sm:h-9 sm:w-9 rounded-md flex items-center justify-center shrink-0 ${accent}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
  </motion.div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [bookings, setBookings] = useState(null);
  const staffRole = ["admin", "operations", "compliance"].includes(user.role);

  useEffect(() => {
    if (user.role !== "tenant") api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, [user.role]);

  useEffect(() => {
    if (staffRole) api.get("/bookings/upcoming?days=14").then((r) => setBookings(r.data)).catch(() => {});
  }, [staffRole]);

  if (user.role === "tenant") return <TenantHome />;
  if (!data) return <div className="overline text-muted-foreground">Loading…</div>;
  const t = data.totals;
  const staff = staffRole;

  return (
    <div className="space-y-8">
      <div>
        <p className="overline text-accent">Welcome, {user.name.split(" ")[0]}</p>
        <h1 className="font-head text-3xl lg:text-4xl font-extrabold tracking-tight mt-1">
          {user.role === "landlord" ? "Your portfolio" : "Operations overview"}
        </h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Stat label="Properties" value={t.properties} icon={Building2} accent="bg-[#18181A] text-white" />
        <Stat label="Live / Market-ready" value={t.live} icon={CheckCircle2} accent="bg-[#2B4C3F]/12 text-[#2B4C3F]" />
        <Stat label="In Onboarding" value={t.onboarding} icon={Clock} accent="bg-[#C6A87C]/20 text-[#8a6d3b]" />
        <Stat label="Open Issues" value={t.open_issues} icon={Wrench} accent="bg-[#D98324]/12 text-[#D98324]" />
        {staff && <Stat label="Awaiting Review" value={t.pending_reviews} icon={ClipboardCheck} accent="bg-[#3a4a6b]/12 text-[#3a4a6b]" />}
        {staff && <Stat label="Owner Leads" value={t.investors} icon={TrendingUp} accent="bg-[#3a4a6b]/12 text-[#3a4a6b]" />}
        {user.role === "landlord"
          ? <Stat label="Expiring Docs" value={t.expiring_docs} icon={AlertTriangle} accent="bg-[#9E382A]/12 text-[#9E382A]" />
          : <Stat label="Owners" value={t.landlords} icon={Users} accent="bg-[#3a4a6b]/12 text-[#3a4a6b]" />}
      </div>

      {staff && data.turnaround_alerts && data.turnaround_alerts.length > 0 && (
        <div className="bg-[#9E382A]/[0.04] border border-[#9E382A]/25 rounded-md" data-testid="turnaround-panel">
          <div className="px-5 py-4 border-b border-[#9E382A]/20 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#9E382A]" />
            <span className="overline text-[#9E382A]">Turnaround — checkouts today needing attention</span>
            <span className="ml-auto text-xs text-muted-foreground">{data.turnaround_alerts.length}</span>
          </div>
          <div className="divide-y divide-[#9E382A]/15">
            {data.turnaround_alerts.map((a) => (
              <div key={a.id} data-testid={`turnaround-row-${a.id}`} onClick={() => nav(`/properties/${a.id}`)} className="px-5 py-3 cursor-pointer hover:bg-[#9E382A]/[0.06] transition-colors flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{a.title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {a.reason === "incomplete"
                      ? `${a.cleaner ? a.cleaner + " logged a clean" : "Clean logged"} — only ${a.done}/${a.total} tasks ticked`
                      : `${a.guest ? `${a.guest} checked out` : "Checked out"} today — no clean logged`}
                  </p>
                </div>
                {a.reason === "incomplete" && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#D98324]/15 text-[#D98324] shrink-0">Incomplete</span>}
                <ArrowUpRight className="h-4 w-4 text-[#9E382A] shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}

      {staff && bookings && (bookings.arrivals.length > 0 || bookings.departures.length > 0) && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="arrivals-departures">
          <div className="bg-white border border-border rounded-md">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <LogIn className="h-4 w-4 text-[#2B4C3F]" /><span className="overline">Arrivals · next 14 days</span>
              <span className="ml-auto text-xs text-muted-foreground">{bookings.arrivals.length}</span>
            </div>
            <div className="divide-y divide-border">
              {bookings.arrivals.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No upcoming arrivals.</p>}
              {bookings.arrivals.map((b) => (
                <div key={b.id} data-testid={`arrival-${b.id}`} onClick={() => nav(`/properties/${b.property_id}`)} className="px-5 py-3 cursor-pointer hover:bg-secondary/50 transition-colors flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{b.guest_name}{b.party_size ? ` · ${b.party_size} guests` : ""}</p>
                    <p className="text-xs text-muted-foreground truncate">{b.property_title}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{fmtDate(b.check_in)}</p>
                    <span className={`text-[0.65rem] px-2 py-0.5 rounded-full capitalize ${BOOKING_STATUS_CLS[b.status]}`}>{b.status.replace("_", " ")}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-border rounded-md">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <LogOut className="h-4 w-4 text-[#C6A87C]" /><span className="overline">Departures · next 14 days</span>
              <span className="ml-auto text-xs text-muted-foreground">{bookings.departures.length}</span>
            </div>
            <div className="divide-y divide-border">
              {bookings.departures.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No upcoming departures.</p>}
              {bookings.departures.map((b) => (
                <div key={b.id} data-testid={`departure-${b.id}`} onClick={() => nav(`/properties/${b.property_id}`)} className="px-5 py-3 cursor-pointer hover:bg-secondary/50 transition-colors flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{b.guest_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{b.property_title}</p>
                  </div>
                  <p className="text-sm font-medium">{fmtDate(b.check_out)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {staff && data.review_properties && data.review_properties.length > 0 && (
        <div className="bg-white border border-[#3a4a6b]/40 rounded-md" data-testid="review-queue">
          <div className="px-5 py-4 border-b border-[#3a4a6b]/20 flex items-center gap-2 bg-[#3a4a6b]/5">
            <ClipboardCheck className="h-4 w-4 text-[#3a4a6b]" />
            <span className="overline text-[#3a4a6b]">Awaiting your review · {t.pending_reviews} task{t.pending_reviews === 1 ? "" : "s"}</span>
          </div>
          <div className="divide-y divide-border">
            {data.review_properties.map((rp) => (
              <div key={rp.id} data-testid={`review-prop-${rp.id}`} onClick={() => nav(`/properties/${rp.id}`)} className="px-5 py-3 cursor-pointer hover:bg-secondary/50 transition-colors flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{rp.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{rp.tasks.join(", ")}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#3a4a6b]/12 text-[#3a4a6b] shrink-0">{rp.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {staff && data.attention_properties && data.attention_properties.length > 0 && (
        <div className="bg-white border border-[#9E382A]/40 rounded-md" data-testid="attention-panel">
          <div className="px-5 py-4 border-b border-[#9E382A]/20 flex items-center gap-2 bg-[#9E382A]/5">
            <AlertTriangle className="h-4 w-4 text-[#9E382A]" />
            <span className="overline text-[#9E382A]">Needs attention · {data.attention_properties.length}</span>
          </div>
          <div className="divide-y divide-border">
            {data.attention_properties.map((a) => (
              <div key={a.id} data-testid={`attention-row-${a.id}`} onClick={() => nav(`/properties/${a.id}`)} className="px-5 py-3 cursor-pointer hover:bg-secondary/50 transition-colors flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{a.title}</p>
                  {a.reason && <p className="text-xs text-[#9E382A] truncate">{a.reason}</p>}
                </div>
                <AlertTriangle className="h-4 w-4 text-[#9E382A] shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}

      {staff && data.onboarding_forms && data.onboarding_forms.length > 0 && (
        <div className="bg-white border border-border rounded-md" data-testid="onboarding-progress">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-[#8a6d3b]" />
            <span className="overline">Onboarding forms · landlord questionnaires</span>
            <span className="ml-auto text-xs text-muted-foreground">{data.onboarding_forms.length}</span>
          </div>
          <div className="divide-y divide-border">
            {data.onboarding_forms.map((f) => {
              const S = {
                sent: { label: "Invited", cls: "bg-[#D98324]/15 text-[#D98324]", when: f.sent_at },
                opened: { label: "Opened", cls: "bg-[#3a4a6b]/12 text-[#3a4a6b]", when: f.opened_at },
                completed: { label: "Completed", cls: "bg-[#2B4C3F]/12 text-[#2B4C3F]", when: f.completed_at },
              }[f.status] || { label: f.status, cls: "bg-secondary text-muted-foreground", when: null };
              return (
                <div key={f.id} data-testid={`onboarding-form-${f.id}`} onClick={() => nav(`/properties/${f.id}`)} className="px-5 py-3 cursor-pointer hover:bg-secondary/50 transition-colors flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{f.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{f.landlord_name || "—"}{S.when ? ` · ${S.label.toLowerCase()} ${fmtDate(S.when)}` : ""}</p>
                  </div>
                  {f.stale && <span data-testid={`onboarding-stale-${f.id}`} className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#9E382A]/12 text-[#9E382A] shrink-0 font-semibold">7d+ no activity</span>}
                  <span className={`text-[0.65rem] px-2 py-0.5 rounded-full shrink-0 ${S.cls}`}>{S.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Action needed */}
        <div className="lg:col-span-2 min-w-0 bg-white border border-border rounded-md">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <span className="overline">Requires Action</span>
            <span className="text-xs text-muted-foreground">{data.action_properties.length} in progress</span>
          </div>
          <div className="divide-y divide-border">
            {data.action_properties.length === 0 && (
              <p className="p-8 text-sm text-muted-foreground text-center">Everything is live. Nothing pending 🎉</p>
            )}
            {data.action_properties.map((p) => (
              <div
                key={p.id}
                data-testid={`action-prop-${p.id}`}
                onClick={() => nav(`/properties/${p.id}`)}
                className="px-4 sm:px-5 py-4 flex items-center gap-3 sm:gap-4 cursor-pointer hover:bg-secondary/50 transition-colors group"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{p.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.address}</p>
                  {p.next_task && (
                    <p className="text-xs mt-1">
                      <span className="text-muted-foreground">Next: </span>
                      <span className="font-medium">{p.next_task}</span>
                      <span className="text-muted-foreground"> · {ROLE_LABELS[p.next_owner]}</span>
                    </p>
                  )}
                </div>
                <div className="w-24 sm:w-32 shrink-0">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-mono">{p.progress.completed}/{p.progress.total}</span>
                    <span className="font-mono">{p.progress.pct}%</span>
                  </div>
                  <Progress value={p.progress.pct} className="h-1.5" />
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block" />
              </div>
            ))}
          </div>
        </div>

        {/* Reminders */}
        <div className="min-w-0 bg-white border border-border rounded-md">
          <div className="px-5 py-4 border-b border-border">
            <span className="overline">Compliance Expiries</span>
          </div>
          <div className="divide-y divide-border">
            {data.reminders.length === 0 && (
              <p className="p-8 text-sm text-muted-foreground text-center">No upcoming expiries.</p>
            )}
            {data.reminders.map((d) => {
              const b = expiryBadge(d.expiry_status);
              return (
                <div key={d.id} onClick={() => nav(`/properties/${d.property_id}`)} className="px-4 sm:px-5 py-3 cursor-pointer hover:bg-secondary/50 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium truncate">{d.doc_type}</p>
                    <span className={`text-[0.65rem] px-2 py-0.5 rounded-full border ${b.cls}`}>{b.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{d.property_title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {d.days_remaining < 0 ? `Expired ${Math.abs(d.days_remaining)}d ago` : `${d.days_remaining}d left`} · {fmtDate(d.expiry_date)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
